// frontend/src/utils/toastMessage.js

/**
 * Toast chrome helpers.
 *
 * End users read a toast as one signal: colour + one icon + a short sentence.
 * Call sites historically prefixed ✅/❌/⚠️/ℹ️ (and sometimes passed the same
 * glyph as `icon`). That stacks a second, OS-dependent mark on top of the
 * helper's icon and looks like a duplicate confirmation.
 *
 * These helpers are the single source of truth for "one icon, no status emoji
 * in the copy". The toast wrapper applies them to every call.
 */

const LEADING_STATUS_EMOJI_RE =
  /^(?:[\uFE0F\uFE0E\u200B\u200C\u200D\u2060]*)(?:✅|❌|❎|⚠️|⚠|ℹ️|ℹ|✔️|✔|✓|❗|🚫|🎉)\s*/u;

const REDUNDANT_STATUS_ICONS = new Set([
  '✅',
  '❌',
  '❎',
  '⚠',
  '⚠️',
  'ℹ',
  'ℹ️',
  '✔',
  '✔️',
  '✓',
  '❗',
  '🚫',
  '🎉',
]);

const classNameToString = (className) => {
  if (typeof className === 'string') return className;
  if (Array.isArray(className)) return className.filter(Boolean).join(' ');
  return '';
};

/**
 * Strip leading status glyphs that only restate toast.success / error / etc.
 *
 * Non-string messages (React nodes, render callbacks) are returned unchanged.
 *
 * @param {*} message
 * @returns {*}
 */
export const stripLeadingToastStatusEmoji = (message) => {
  if (typeof message !== 'string') return message;

  let next = message;
  let previous;
  do {
    previous = next;
    next = next.replace(LEADING_STATUS_EMOJI_RE, '');
  } while (next !== previous);

  return next;
};

/**
 * True when `icon` is a status emoji that duplicates the helper's own mark.
 * Contextual icons (thermometer, clock) are kept.
 *
 * @param {*} icon
 * @returns {boolean}
 */
export const isRedundantToastStatusIcon = (icon) => {
  if (typeof icon !== 'string') return false;
  const trimmed = icon.trim();
  if (REDUNDANT_STATUS_ICONS.has(trimmed)) return true;
  const withoutVariation = trimmed.replace(/[\uFE0F\uFE0E]/g, '');
  return REDUNDANT_STATUS_ICONS.has(withoutVariation);
};

/**
 * Normalize a toast() call so the renderer sees clean copy and at most one icon.
 *
 * @param {*} message
 * @param {object} [options]
 * @returns {[*, object|undefined]}
 */
export const normalizeToastCall = (message, options) => {
  const nextMessage = stripLeadingToastStatusEmoji(message);
  if (!options || !isRedundantToastStatusIcon(options.icon)) {
    return [nextMessage, options];
  }

  const { icon: _redundantIcon, ...rest } = options;
  return [nextMessage, rest];
};

/**
 * Decide which single icon slot a toast should render.
 *
 * @param {{ type?: string, className?: string|string[], icon?: * }} toastLike
 * @returns {{ slot: 'provided'|'success'|'error'|'warning'|'info'|'none', icon?: * }}
 */
export const resolveToastIconSlot = ({ type, className, icon } = {}) => {
  if (type === 'loading') {
    return { slot: 'provided', icon };
  }

  const usableIcon = isRedundantToastStatusIcon(icon) ? null : icon;
  if (usableIcon) {
    return { slot: 'provided', icon: usableIcon };
  }

  const cls = classNameToString(className);
  if (type === 'success' || cls.includes('toast-success')) {
    return { slot: 'success' };
  }
  if (type === 'error' || cls.includes('toast-error')) {
    return { slot: 'error' };
  }
  if (cls.includes('toast-warning')) {
    return { slot: 'warning' };
  }
  if (cls.includes('toast-info')) {
    return { slot: 'info' };
  }

  return { slot: 'none' };
};
